import { useState } from 'react';

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  DialogActions,
  Alert,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import Grid from '@mui/material/Grid';
import { LoadingButton } from '@mui/lab';
import CloseIcon from '@mui/icons-material/Close';
import type { EntityId } from '@/types';

interface ChartDeleteDialogProps {
  chart_ids?: EntityId[];
  chart_names?: string[];
  deleteCharts: (chart_ids: EntityId[]) => Promise<void>;
  open: boolean;
  handleClose: () => void;
}

function ChartDeleteDialog({
  chart_ids = [],
  chart_names,
  deleteCharts,
  open,
  handleClose,
}: ChartDeleteDialogProps): React.JSX.Element {
  const [loading, setLoading] = useState<boolean>(false);
  const [api_error, setApiError] = useState<string | null>(null);

  const handleSubmit = async (): Promise<void> => {
    if (!chart_ids || !chart_ids.length) return;
    try {
      setLoading(true);
      await deleteCharts(chart_ids);
      setLoading(false);
      handleClose();
    } catch (error) {
      console.error('Error deleting charts:', error);
      const err = error as Error;
      setApiError(
        err.message ||
          'Unable to delete the chart(s). This could be due to network issues, server problems, or insufficient permissions.',
      );
      setLoading(false);
    }
  };

  const ids = chart_ids ?? [];
  const names = chart_names ?? ids.map((id) => String(id));

  return (
    <Dialog
      onClose={() => handleClose()}
      open={open}
      PaperProps={{
        sx: { maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
      }}
    >
      <DialogTitle sx={{ flexShrink: 0 }}>
        <Grid container direction='row' alignItems='center'>
          <Grid size={11}>
            Delete Chart{ids.length !== 1 ? 's' : ''}
          </Grid>
          <Grid size={1} sx={{ maxWidth: '25px' }}>
            <IconButton aria-label='close' onClick={() => handleClose()}>
              <CloseIcon />
            </IconButton>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <Alert severity='error'>
          This action is permanent and cannot be reversed. All selected
          charts and their configured sections, waypoints, and elements will
          be permanently deleted. There is no way to recover them. Are you
          sure you want to continue?
        </Alert>
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Typography variant='subtitle2' color='text.secondary'>
            Selected ({names.length}):
          </Typography>
          <List
            dense
            disablePadding
            sx={{
              maxHeight: 'min(40vh, 200px)',
              overflow: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {names.map((name, i) => (
              <ListItem key={ids[i] ?? i}>
                <ListItemText primary={name} primaryTypographyProps={{ noWrap: true }} />
              </ListItem>
            ))}
          </List>
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexShrink: 0 }}>
        <Button color='inherit' onClick={handleClose} sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Box sx={{ flex: 1 }} />
        <LoadingButton
          loading={loading}
          variant='contained'
          color='primary'
          onClick={handleSubmit}
          disabled={!ids.length}
        >
          Delete
        </LoadingButton>
      </DialogActions>

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </Dialog>
  );
}

export default ChartDeleteDialog;

